/**
 * The embedded profile controls and their native ribbon twins target the same
 * destinations, so both reach workspace state through the one navigation guard.
 * This hook is the embedded half: it wraps the raw gate handlers so a selection
 * or a load can only happen inside a guarded commit.
 */

import { useCallback } from 'react';
import {
  requestGuardedProfileChange,
  requestGuardedProfileLoad,
} from '../lib/guardedProfileNavigation';

export interface UseGuardedProfileActionsArgs {
  selectedProfileKey: string;
  lastAppliedProfileKey: string;
  /** Raw gate handlers — only ever called from inside a guarded commit. */
  applySelectedProfileChange: (profileKey: string) => void;
  loadProfileForCurrentProject: (profileKeyOverride?: string) => Promise<boolean>;
  /** Destination-specific consent, asked from inside the guarded transaction. */
  confirmProfileLoad: (profileKeyOverride?: string) => Promise<boolean>;
}

export interface GuardedProfileActions {
  /** Selects a profile once the guard has settled any unsaved edits. */
  handleGuardedProfileChange: (profileKey: string) => void;
  /** Loads a profile the same way, reporting whether the profile came back. */
  handleGuardedProfileLoad: (profileKeyOverride?: string) => Promise<boolean>;
}

export const useGuardedProfileActions = ({
  selectedProfileKey,
  lastAppliedProfileKey,
  applySelectedProfileChange,
  loadProfileForCurrentProject,
  confirmProfileLoad,
}: UseGuardedProfileActionsArgs): GuardedProfileActions => {
  const handleGuardedProfileChange = useCallback(
    (profileKey: string): void => {
      const target = profileKey.trim();
      if (!target) {
        return;
      }

      void requestGuardedProfileChange(target, 'profileSelection', () => {
        applySelectedProfileChange(target);
      });
    },
    [applySelectedProfileChange]
  );

  const handleGuardedProfileLoad = useCallback(
    (profileKeyOverride?: string): Promise<boolean> => {
      const profileKey = profileKeyOverride?.trim() || selectedProfileKey;
      if (!profileKey) {
        return Promise.resolve(false);
      }

      // Reloading the profile that is already applied replaces nothing, so it
      // needs no extra consent — the guard still decides whether it may run.
      const reloadingApplied = profileKey === lastAppliedProfileKey;

      return requestGuardedProfileLoad(
        profileKey,
        'profileLoad',
        () => loadProfileForCurrentProject(profileKey),
        reloadingApplied ? undefined : { confirm: () => confirmProfileLoad(profileKey) }
      );
    },
    [confirmProfileLoad, lastAppliedProfileKey, loadProfileForCurrentProject, selectedProfileKey]
  );

  return { handleGuardedProfileChange, handleGuardedProfileLoad };
};
